import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { cn } from '../lib/utils';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function fmtInr(amount) {
  const n = Number(amount) || 0;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatSubmitted(iso) {
  const ms = new Date(iso).getTime();
  if (!Number.isFinite(ms)) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function equipmentCategoryLabel(t) {
  return t('regEquipmentCategory').replace(/\s*\*$/, '');
}

function approvalBadgeClass(status) {
  if (status === 'approved') {
    return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400';
  }
  if (status === 'rejected') {
    return 'border-de-accent/40 bg-de-accent/15 text-de-accent';
  }
  return 'border-amber-500/40 bg-amber-500/15 text-amber-400';
}

function approvalLabelKey(status) {
  if (status === 'approved') return 'marketRateApproved';
  if (status === 'rejected') return 'marketRateRejected';
  return 'marketRatePending';
}

export default function AdminMarketRatePanel({ authToken }) {
  const { t } = useLanguage();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [machineTypeFilter, setMachineTypeFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [reviewingId, setReviewingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ min_rate_per_hour: '', max_rate_per_hour: '' });
  const [deletedMessage, setDeletedMessage] = useState('');
  const [reviewedMessage, setReviewedMessage] = useState('');
  const [editValidationError, setEditValidationError] = useState('');

  const loadMarketRates = useCallback(async () => {
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      setRates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/market-rates`,
        { headers: { Authorization: `Bearer ${authToken}` } },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setRates(Array.isArray(json.rates) ? json.rates : []);
    } catch {
      setRates([]);
      setError(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [authToken, t]);

  useEffect(() => {
    loadMarketRates();
  }, [loadMarketRates]);

  const machineTypeOptions = useMemo(() => {
    const values = [...new Set(rates.map((row) => row.machine_type).filter(Boolean))];
    return values.sort((a, b) => String(a).localeCompare(String(b)));
  }, [rates]);

  const cityOptions = useMemo(() => {
    const values = [...new Set(rates.map((row) => row.city).filter(Boolean))];
    return values.sort((a, b) => String(a).localeCompare(String(b)));
  }, [rates]);

  const filteredRates = useMemo(() => {
    return rates.filter((row) => {
      if (machineTypeFilter && row.machine_type !== machineTypeFilter) return false;
      if (cityFilter && row.city !== cityFilter) return false;
      const status = row.approval_status || 'pending';
      if (approvalFilter && status !== approvalFilter) return false;
      return true;
    });
  }, [rates, machineTypeFilter, cityFilter, approvalFilter]);

  const openEditApprove = (row) => {
    setEditingId(row.id);
    setEditForm({
      min_rate_per_hour: row.min_rate_per_hour != null ? String(row.min_rate_per_hour) : '',
      max_rate_per_hour: row.max_rate_per_hour != null ? String(row.max_rate_per_hour) : '',
    });
    setError('');
    setEditValidationError('');
    setDeletedMessage('');
    setReviewedMessage('');
  };

  const closeEditApprove = () => {
    setEditingId(null);
    setEditForm({ min_rate_per_hour: '', max_rate_per_hour: '' });
    setEditValidationError('');
  };

  const handleDelete = async (rateId) => {
    if (!window.confirm(t('adminMarketRateDeleteConfirm'))) return;
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      return;
    }
    setDeletingId(rateId);
    setDeletedMessage('');
    setReviewedMessage('');
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/admin/market-rates/${encodeURIComponent(rateId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      setRates((prev) => prev.filter((row) => row.id !== rateId));
      if (editingId === rateId) closeEditApprove();
      setDeletedMessage(t('adminMarketRateDeleted'));
    } catch {
      setError(t('failedToLoad'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleReview = async (rateId, decision, rateOverrides = null) => {
    if (!authToken) {
      setError(t('sessionExpiredRefresh'));
      return;
    }
    setReviewingId(rateId);
    setDeletedMessage('');
    setReviewedMessage('');
    setError('');
    try {
      const body = { decision };
      if (rateOverrides) {
        body.min_rate_per_hour = rateOverrides.min_rate_per_hour;
        body.max_rate_per_hour = rateOverrides.max_rate_per_hour;
      }
      const res = await fetch(
        `${API_BASE_URL}/api/admin/market-rates/${encodeURIComponent(rateId)}`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        },
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(json.error || 'Failed');
      }
      closeEditApprove();
      setReviewedMessage(t('marketRateReviewed'));
      await loadMarketRates();
    } catch {
      setError(t('failedToLoad'));
    } finally {
      setReviewingId(null);
    }
  };

  const handleEditApproveSubmit = async (rateId) => {
    const minRate = Number(editForm.min_rate_per_hour);
    const maxRate = Number(editForm.max_rate_per_hour);
    if (!Number.isFinite(minRate) || minRate <= 0) {
      setEditValidationError(t('marketRateEditMin'));
      return;
    }
    if (!Number.isFinite(maxRate) || maxRate <= 0 || maxRate <= minRate) {
      setEditValidationError(t('marketRateEditMax'));
      return;
    }
    setEditValidationError('');
    await handleReview(rateId, 'approved', {
      min_rate_per_hour: minRate,
      max_rate_per_hour: maxRate,
    });
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="de-stat-ring h-10 w-10">
            <MapPin className="h-5 w-5 text-de-gold" />
          </div>
          <h2 className="m-0 text-lg font-bold text-de-gold">{t('adminMarketRates')}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-de-muted">
            <span>{equipmentCategoryLabel(t)}:</span>
            <select
              value={machineTypeFilter}
              onChange={(e) => setMachineTypeFilter(e.target.value)}
              className="rounded-lg border border-de-gold/35 bg-black/20 px-2 py-1.5 text-xs text-de-text"
            >
              <option value="">{t('adminKycAllFilter')}</option>
              {machineTypeOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-de-muted">
            <span>{t('marketCity')}:</span>
            <select
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="rounded-lg border border-de-gold/35 bg-black/20 px-2 py-1.5 text-xs text-de-text"
            >
              <option value="">{t('adminKycAllFilter')}</option>
              {cityOptions.map((value) => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs text-de-muted">
            <span>{t('status')}:</span>
            <select
              value={approvalFilter}
              onChange={(e) => setApprovalFilter(e.target.value)}
              className="rounded-lg border border-de-gold/35 bg-black/20 px-2 py-1.5 text-xs text-de-text"
            >
              <option value="">{t('adminKycAllFilter')}</option>
              <option value="pending">{t('marketRatePending')}</option>
              <option value="approved">{t('marketRateApproved')}</option>
              <option value="rejected">{t('marketRateRejected')}</option>
            </select>
          </label>
        </div>
      </div>

      {deletedMessage ? (
        <p className="m-0 text-sm text-emerald-400">{deletedMessage}</p>
      ) : null}
      {reviewedMessage ? (
        <p className="m-0 text-sm text-emerald-400">{reviewedMessage}</p>
      ) : null}

      <div className="de-ultra-card overflow-hidden">
        <div className="border-b border-de-gold/15 px-5 py-4">
          <h3 className="m-0 text-sm font-bold text-white">{t('adminMarketRates')}</h3>
        </div>
        <div className="p-4">
          {loading ? (
            <p className="m-0 text-sm text-de-muted">{t('loading')}</p>
          ) : error ? (
            <div>
              <p className="m-0 mb-3 text-sm text-de-accent">{error}</p>
              <button
                type="button"
                onClick={loadMarketRates}
                className="rounded-lg border border-de-gold/40 bg-de-gold/10 px-4 py-2 text-xs font-semibold text-de-gold"
              >
                {t('retryLabel')}
              </button>
            </div>
          ) : rates.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminMarketRateNoData')}</p>
          ) : filteredRates.length === 0 ? (
            <p className="m-0 text-sm text-de-muted">{t('adminMarketRateNoData')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1040px] border-collapse text-xs">
                <thead>
                  <tr className="border-b border-white/10">
                    {[
                      t('adminFleetOwnerCol'),
                      t('phone'),
                      equipmentCategoryLabel(t),
                      t('marketCity'),
                      t('marketMinRate'),
                      t('marketMaxRate'),
                      t('dieselColDate'),
                      t('status'),
                      t('chipAction'),
                    ].map((h) => (
                      <th key={h} className="px-2 py-2 text-left font-semibold text-de-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredRates.map((row) => {
                    const status = row.approval_status || 'pending';
                    const busy = deletingId === row.id || reviewingId === row.id;
                    const isEditing = editingId === row.id;
                    return (
                      <React.Fragment key={row.id}>
                        <tr className="border-b border-white/5">
                          <td className="px-2 py-2 text-de-text">{row.owner_name || '—'}</td>
                          <td className="px-2 py-2 text-de-text">{row.owner_phone || '—'}</td>
                          <td className="px-2 py-2 text-de-text">{row.machine_type || '—'}</td>
                          <td className="px-2 py-2 text-de-text">{row.city || '—'}</td>
                          <td className="px-2 py-2 font-bold text-de-gold">{fmtInr(row.min_rate_per_hour)}/hr</td>
                          <td className="px-2 py-2 font-bold text-de-gold">{fmtInr(row.max_rate_per_hour)}/hr</td>
                          <td className="px-2 py-2 text-de-muted">{formatSubmitted(row.created_at)}</td>
                          <td className="px-2 py-2">
                            <span
                              className={cn(
                                'inline-flex rounded border px-2 py-0.5 text-[10px] font-semibold',
                                approvalBadgeClass(status),
                              )}
                            >
                              {t(approvalLabelKey(status))}
                            </span>
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {status === 'pending' ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => handleReview(row.id, 'approved')}
                                    className={cn(
                                      'rounded border px-2 py-1 text-[10px] font-semibold transition-colors',
                                      busy
                                        ? 'cursor-not-allowed border-white/10 text-de-muted'
                                        : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
                                    )}
                                  >
                                    {t('marketRateApprove')}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => (
                                      isEditing ? closeEditApprove() : openEditApprove(row)
                                    )}
                                    className={cn(
                                      'rounded border px-2 py-1 text-[10px] font-semibold transition-colors',
                                      busy
                                        ? 'cursor-not-allowed border-white/10 text-de-muted'
                                        : 'border-de-gold/40 bg-de-gold/10 text-de-gold hover:bg-de-gold/20',
                                    )}
                                  >
                                    {t('marketRateEditApprove')}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={busy}
                                    onClick={() => handleReview(row.id, 'rejected')}
                                    className={cn(
                                      'rounded border px-2 py-1 text-[10px] font-semibold transition-colors',
                                      busy
                                        ? 'cursor-not-allowed border-white/10 text-de-muted'
                                        : 'border-amber-500/40 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20',
                                    )}
                                  >
                                    {t('marketRateReject')}
                                  </button>
                                </>
                              ) : status === 'approved' ? (
                                <span className="text-[10px] font-semibold text-emerald-400">
                                  ✅ {t('marketRateApproved')}
                                </span>
                              ) : (
                                <span className="text-[10px] font-semibold text-de-accent">
                                  ❌ {t('marketRateRejected')}
                                </span>
                              )}
                              <button
                                type="button"
                                disabled={busy}
                                onClick={() => handleDelete(row.id)}
                                className={cn(
                                  'rounded border px-2 py-1 text-[10px] font-semibold transition-colors',
                                  busy
                                    ? 'cursor-not-allowed border-white/10 text-de-muted'
                                    : 'border-de-accent/40 bg-de-accent/10 text-de-accent hover:bg-de-accent/20',
                                )}
                              >
                                {t('adminMarketRateDelete')}
                              </button>
                            </div>
                          </td>
                        </tr>
                        {isEditing && status === 'pending' ? (
                          <tr className="border-b border-white/5 bg-black/20">
                            <td colSpan={9} className="px-3 py-3">
                              {editValidationError ? (
                                <p className="m-0 mb-2 text-[11px] text-de-accent">{editValidationError}</p>
                              ) : null}
                              <div className="flex flex-wrap items-end gap-3">
                                <label className="flex flex-col gap-1 text-[10px] text-de-muted">
                                  <span>{t('marketRateEditMin')}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="50"
                                    value={editForm.min_rate_per_hour}
                                    onChange={(e) => setEditForm((prev) => ({
                                      ...prev,
                                      min_rate_per_hour: e.target.value,
                                    }))}
                                    className="rounded-lg border border-de-gold/35 bg-black/30 px-2 py-1.5 text-xs text-de-text"
                                  />
                                </label>
                                <label className="flex flex-col gap-1 text-[10px] text-de-muted">
                                  <span>{t('marketRateEditMax')}</span>
                                  <input
                                    type="number"
                                    min="0"
                                    step="50"
                                    value={editForm.max_rate_per_hour}
                                    onChange={(e) => setEditForm((prev) => ({
                                      ...prev,
                                      max_rate_per_hour: e.target.value,
                                    }))}
                                    className="rounded-lg border border-de-gold/35 bg-black/30 px-2 py-1.5 text-xs text-de-text"
                                  />
                                </label>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={() => handleEditApproveSubmit(row.id)}
                                  className={cn(
                                    'rounded border px-3 py-1.5 text-[10px] font-semibold transition-colors',
                                    busy
                                      ? 'cursor-not-allowed border-white/10 text-de-muted'
                                      : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20',
                                  )}
                                >
                                  {t('marketRateEditApprove')}
                                </button>
                                <button
                                  type="button"
                                  disabled={busy}
                                  onClick={closeEditApprove}
                                  className="rounded border border-white/15 bg-white/5 px-3 py-1.5 text-[10px] font-semibold text-de-muted"
                                >
                                  {t('cancel')}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
